import {
  type ThemePaletteColor,
  createTheme as createMuiTheme,
} from '@joggrdocs/riker-theme';
import { colors } from '@mui/material';
import {
  type PaletteOptions as MuiPalette,
  type TypeBackground as MuiThemePaletteBackground,
  darken,
} from '@mui/material/styles';
import _ from 'lodash';
import { match } from 'ts-pattern';
import { convertToMuiThemeMode } from '../internals/utils';
import type { ThemeMode } from '../types';
import {
  type ThemeBaseVariables,
  getThemeBaseVariables,
  getThemeVariablesByName,
} from '../variables.gen';

export interface ThemeBackground {
  dashboardTopBar: string;
  dashboardSideBar: string;
  dashboardMain: string;
}

export interface ThemeCodeEditorColorPalette {
  background: string;
  textSelection: string;
}

export interface ThemeCustomPalette<M extends string = ThemeMode>
  extends Omit<MuiPalette, 'background' | 'mode'> {
  mode: M;
  generate: ThemePaletteColor;
  codeEditor: ThemeCodeEditorColorPalette;
  background: ThemeBackground & MuiThemePaletteBackground;
}

/**
 * Create a palette for a given theme mode, that supports CSS variables.
 *
 * @param mode A theme mode.
 * @returns A valid palette, that supports CSS variables.
 */
export function createPalette(mode: ThemeMode): ThemeCustomPalette {
  const { palette } = createMuiTheme(convertToMuiThemeMode(mode));
  const cssVar = createCssVar(mode);

  const customPalette = match(mode)
    .with('twilight', () => ({
      neutral: {
        light: palette.grey[600],
        main: palette.grey[700],
        dark: palette.grey[900],
        contrastText: palette.primary.contrastText,
      },
      generate: {
        light: '#ED5FE3',
        main: '#e937dc',
        dark: '#A3269A',
        200: '#f180e7',
        800: '#9800bb',
        contrastText: palette.primary.contrastText,
      },
      codeEditor: {
        background: colors.blueGrey[900],
        textSelection: colors.blueGrey[700],
      },
      background: {
        paper: cssVar('--color-background-paper'),
        default: cssVar('--color-background-default'),
        dashboardTopBar: '#111936',
        dashboardSideBar: '#111936',
        dashboardMain: cssVar('--color-background-paper'),
      },
    }))
    .with('dark', () => ({
      neutral: {
        light: palette.grey[100],
        main: palette.grey[200],
        dark: palette.grey[400],
        contrastText: palette.primary.contrastText,
      },
      generate: {
        light: '#ED5FE3',
        main: '#e937dc',
        dark: '#A3269A',
        200: '#f180e7',
        800: '#9800bb',
        contrastText: palette.primary.contrastText,
      },
      codeEditor: {
        background: colors.blueGrey[900],
        textSelection: colors.blueGrey[700],
      },
      text: {
        primary: cssVar('--color-primary-contrast'),
        secondary: cssVar('--color-secondary-contrast'),
        disabled: cssVar('--color-grey-500'),
      },
      background: {
        dashboardTopBar: cssVar('--color-background-default'),
        dashboardSideBar: cssVar('--color-background-default'),
        dashboardMain: cssVar('--color-background-paper'),
        paper: cssVar('--color-background-paper'),
        default: cssVar('--color-background-default'),
      },
    }))
    .with('light', () => ({
      neutral: {
        light: palette.grey[100],
        main: palette.grey[200],
        dark: palette.grey[400],
        contrastText: palette.primary.contrastText,
      },
      generate: {
        light: '#E733DE',
        main: '#e100d6',
        dark: '#9D0095',
        200: '#f08de7',
        contrastText: palette.background.paper,
      },
      codeEditor: {
        background: palette.background.paper,
        textSelection: palette.grey[200],
      },
      background: {
        paper: cssVar('--color-background-paper'),
        default: cssVar('--color-background-default'),
        dashboardTopBar: darken(palette.grey[50], 0.025),
        dashboardSideBar: darken(palette.grey[50], 0.025),
        dashboardMain: palette.background.paper,
      },
    }))
    .exhaustive();

  return {
    mode,
    common: palette.common,
    primary: palette.primary,
    secondary: palette.secondary,
    error: palette.error,
    warning: palette.warning,
    info: palette.info,
    success: palette.success,
    grey: palette.grey,
    text: palette.text,
    divider: palette.divider,
    action: palette.action,
    ...customPalette,
  };
}

declare module '@mui/material/styles' {
  interface Palette {
    neutral: ThemePaletteColor;
    generate: ThemePaletteColor;
    codeEditor: ThemeCodeEditorColorPalette;
  }
  interface PaletteOptions {
    neutral?: ThemePaletteColor;
    generate?: ThemePaletteColor;
    codeEditor?: ThemeCodeEditorColorPalette;
  }
  interface TypeBackground extends ThemeBackground {}
}

/*
|------------------
| Internals
|------------------
*/

function createCssVar(mode: ThemeMode) {
  const cssVars = getCssPalette(mode);
  return (variable: keyof ThemeBaseVariables) => {
    const foundCssVar = cssVars[variable];

    if (!foundCssVar) {
      throw new Error(`CSS variable ${variable} not found in ${mode} mode`);
    }

    return foundCssVar;
  };
}

function getCssPalette(mode: ThemeMode): ThemeBaseVariables {
  const cssVars = getThemeVariablesByName(mode);
  const baseCssVars = getThemeBaseVariables();
  const allCssVars = {
    ...baseCssVars,
    ...cssVars,
  };
  return _.chain(allCssVars)
    .mapValues((value, key, obj) => {
      if (!value.includes('var(')) {
        return value;
      }
      return getCssVar(obj, value);
    })
    .omitBy((_value, key) => key.startsWith('--sg-color-'))
    .value() as ThemeBaseVariables;
}

function getCssVar(themeVariables: Record<string, string>, cssVar: string) {
  const foundCssVar = themeVariables[unwrapCssVar(cssVar)];
  if (foundCssVar?.includes('var(')) {
    return getCssVar(themeVariables, unwrapCssVar(foundCssVar));
  }
  return foundCssVar;
}

function unwrapCssVar(cssVar: string) {
  const foundCssVar = cssVar.match(/var\((.*)\)/);
  if (foundCssVar !== null) {
    return foundCssVar[1];
  }
  return cssVar;
}
